import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef } from "react";
import type { EntityKind } from "../Dashboard/types";
import {
  publishWatcherState,
  retainPrefetcher,
  unregisterWatcher,
  type WatcherItem,
} from "./rmsPrefetching";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";

// Spread onto an element to mark it for prefetching by useVisibilityPrefetch
// (when an observer in an ancestor is watching that subtree).
export const prefetchAttrs = (target: {
  kind: string;
  id: string;
}): {
  "data-prefetch-entity"?: string;
  "data-prefetch-interaction"?: string;
} => {
  if (target.kind === "interaction") {
    return { "data-prefetch-interaction": target.id };
  }
  return { "data-prefetch-entity": `${target.kind}:${target.id}` };
};

const parsePrefetchAttrs = (element: Element): WatcherItem | null => {
  const entityAttr = element.getAttribute("data-prefetch-entity");
  if (entityAttr) {
    const [kind, id] = entityAttr.split(":");
    if (kind && id) {
      return {
        type: "entity",
        kind: kind as EntityKind,
        id,
        timestamp: Date.now(),
      };
    }
  }
  const interactionId = element.getAttribute("data-prefetch-interaction");
  if (interactionId) {
    return { type: "interaction", id: interactionId, timestamp: Date.now() };
  }
  return null;
};

// IntersectionObserver-driven watcher: tracks which prefetchable elements
// inside `containerRef` are currently in view and publishes the snapshot to
// the global prefetcher on every change. One instance per page surface —
// RMSHeader, EntityDashboard, TablePanel each mount their own.
export const useVisibilityPrefetch = (
  containerRef?: React.RefObject<HTMLElement>
) => {
  const queryClient = useQueryClient();
  const watcherId = useId();
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  // Keyed by the DOM element so we can remove entries when they leave view.
  const visibleRef = useRef<Map<Element, WatcherItem>>(new Map());

  const activeContainerRef = containerRef || internalContainerRef;

  const publish = useCallback(() => {
    publishWatcherState(
      watcherId,
      queryClient,
      Array.from(visibleRef.current.values())
    );
  }, [queryClient, watcherId]);

  const setupObserver = useCallback(() => {
    const container = activeContainerRef.current;
    if (!container) return;

    // Tear down any prior setup so re-invocations (mount retry, post-load
    // kick from EntityDashboard) don't stack observers.
    observerRef.current?.disconnect();
    mutationObserverRef.current?.disconnect();
    visibleRef.current.clear();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const item = parsePrefetchAttrs(entry.target);
            if (item) {
              visibleRef.current.set(entry.target, item);
              changed = true;
            }
          } else if (visibleRef.current.delete(entry.target)) {
            changed = true;
          }
        }
        if (changed) publish();
      },
      {
        rootMargin: "50px",
        threshold: 0.1,
      }
    );

    const observeElements = () => {
      const elements = container.querySelectorAll(
        "[data-prefetch-entity], [data-prefetch-interaction]"
      );
      elements.forEach((el) => observerRef.current?.observe(el));
    };

    observeElements();

    mutationObserverRef.current = new MutationObserver(observeElements);
    mutationObserverRef.current.observe(container, {
      childList: true,
      subtree: true,
    });
  }, [activeContainerRef, publish]);

  useEffect(() => {
    // The prefetcher's scroll listener belongs to the mounted watchers, not to
    // this module's import (#12187) — retain it here and let the cleanup drop
    // it along with everything else this effect started.
    const releasePrefetcher = retainPrefetcher(queryClient);
    setupObserver();
    // Containers sometimes aren't populated on the first render; retry once.
    // (The container ref not yet holding a node when this effect first runs
    // is the actual gap -- once it exists, the MutationObserver setupObserver
    // installs already catches later additions. Fixing that at the source
    // would mean turning the ref this hook hands back into a callback ref,
    // which reaches every consumer of `containerRef`; kept as a timer here.)
    const timeoutId = setProductTimeout(() => setupObserver(), 100);

    return () => {
      clearProductTimeout(timeoutId);
      observerRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
      visibleRef.current.clear();
      unregisterWatcher(watcherId, queryClient);
      releasePrefetcher();
    };
  }, [queryClient, setupObserver, watcherId]);

  return {
    containerRef: internalContainerRef,
    setupObserver,
  };
};
